import React, { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@Sage/hoi-poi-ui';
import Header from 'containers/components/layouts/Header';
import HeaderTitle from 'containers/components/layouts/HeaderTitle';
import HeaderSection from 'containers/components/layouts/HeaderSection';
import HeaderSectionDefault from 'containers/components/layouts/HeaderSectionDefault';
import ViewType from 'containers/components/ViewType';
import {
    canAccessEmailCampaigns,
    canAccessSalesCampaigns,
    shouldShowCampaignsTabs,
} from 'containers/campaigns/utils/campaignPermissions';
import { getLiteral } from 'utils/getLiteral';
import colors from 'constants/colors';

const TAB_CONFIG = [
    {
        id: 'activity',
        path: '/campaigns/activity',
        titleKey: 'label_sales_campaigns',
        iconName: 'activities',
    },
    {
        id: 'marketing',
        path: '/campaigns/marketing',
        titleKey: 'label_email_campaigns',
        iconName: 'emailSendOutline',
    },
];

const CampaignsHeader = React.memo(() => {
    const hasSalesCampaigns = useSelector(canAccessSalesCampaigns);
    const hasEmailCampaigns = useSelector(canAccessEmailCampaigns);
    const showTabs = useSelector(shouldShowCampaignsTabs);
    const location = useLocation();
    const navigate = useNavigate();

    const activeSection = useMemo(() => {
        if (hasEmailCampaigns && !hasSalesCampaigns) {
            return 1;
        }
        if (hasSalesCampaigns && !hasEmailCampaigns) {
            return 0;
        }

        const pathname = location.pathname;
        if (pathname.startsWith('/campaigns/marketing')) {
            return 1;
        }
        return 0;
    }, [hasEmailCampaigns, hasSalesCampaigns, location.pathname]);

    const handleTabChange = useCallback(
        (event, index) => {
            if (!showTabs) return;
            const targetPath = TAB_CONFIG[index]?.path;
            if (targetPath && location.pathname !== targetPath) {
                navigate(targetPath);
            }
        },
        [location.pathname, navigate, showTabs],
    );

    const tabs = useMemo(() => {
        if (!showTabs) return [];

        const activeColor = colors.actionMinor[500];
        const inactiveColor = colors.actionMinor[200];

        return TAB_CONFIG.map((config, index) => {
            const color = activeSection === index ? activeColor : inactiveColor;
            return {
                title: getLiteral(config.titleKey),
                icon: <Icon name={config.iconName} color={color} />,
            };
        });
    }, [activeSection, showTabs]);

    return (
        <Header>
            <HeaderSection>
                <HeaderTitle title={TAB_CONFIG[activeSection]?.titleKey} />
            </HeaderSection>
            {showTabs && (
                <ViewType onChange={handleTabChange} section={activeSection} tabs={tabs} />
            )}
            <HeaderSectionDefault />
        </Header>
    );
});

export default CampaignsHeader;
